import { useState } from 'react';
import { session } from '../../lib/api/session.js';

const AUTH_BASE = import.meta.env.VITE_AUTH_URL ?? 'https://auth.voxfauna.org';
const CLIENT_ID = import.meta.env.VITE_AUTH_CLIENT_ID ?? 'voxfauna';

/**
 * Email one-time-code sign-in.
 *
 * VoxFauna holds no credentials of its own — this talks directly to the shared
 * auth-service over public HTTPS. The API is a pure resource server: it verifies
 * the resulting token against the provider's published keys and never issues
 * one.
 *
 * `X-Client-Id` is what tells the auth-service which product is asking, so it
 * uses VoxFauna's own user table, branding and token audience rather than
 * FidiPerks'. Without it the request is served as the default client and the
 * resulting token is rejected by the API on audience grounds.
 *
 * A one-time code rather than a password because there is no password to
 * remember, no reset flow to build, and nothing for VoxFauna to leak.
 */
export function SignInPage() {
  const [stage, setStage] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const call = async (path: string, body: unknown): Promise<Record<string, unknown>> => {
    // auth.voxfauna.org is a CloudFront distribution whose Origin Path (/auth) rewrites this
    // onto the real auth-service's app.use('/auth', ...) mount — no prefix needed here.
    let response: Response;
    try {
      response = await fetch(`${AUTH_BASE}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Client-Id': CLIENT_ID },
        body: JSON.stringify(body),
      });
    } catch {
      // fetch rejects with a bare TypeError ("Failed to fetch" / "Load failed")
      // for offline, DNS and blocked requests — never worth showing as written.
      throw new Error("We couldn't reach the sign-in service. Check your connection and try again.");
    }

    const payload = (await response.json().catch(() => null)) as Record<string, unknown> | null;

    if (!response.ok) {
      const message =
        (typeof payload?.error === 'string' && payload.error) ||
        (typeof payload?.message === 'string' && payload.message) ||
        'Something went wrong. Please try again.';
      throw new Error(message);
    }

    return (payload?.data as Record<string, unknown>) ?? payload ?? {};
  };

  const sendCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await call('/v1/otp/send', { identifier: email.trim().toLowerCase(), purpose: 'login' });
      setStage('code');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send a code.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const identifier = email.trim().toLowerCase();

      // Two-step by design: /v1/otp/verify proves the code was received and
      // returns a short-lived verifiedToken; /v1/otp/login then requires that
      // token rather than accepting an identifier alone, so a bare identifier
      // can never mint a session.
      const verifyResult = await call('/v1/otp/verify', {
        identifier,
        otpCode: code.trim(),
        purpose: 'login',
      });
      const verifiedToken = verifyResult.verifiedToken;
      if (typeof verifiedToken !== 'string') {
        throw new Error('Code verification did not return a verifiedToken.');
      }

      const data = await call('/v1/otp/login', { identifier, verifiedToken });

      const token = data.token;
      const refreshToken = data.refreshToken;

      if (typeof token !== 'string' || typeof refreshToken !== 'string') {
        // A login with no refresh token is the forced-logout problem the
        // auth-service migration exists to fix: the access token expires in
        // ~30 minutes and there is nothing to renew it with. Fail loudly
        // rather than signing someone in for half an hour.
        throw new Error(
          'Sign-in did not return a refresh token. The auth-service needs to issue one on this login path.',
        );
      }

      session.start({ token, refreshToken });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That code did not work.');
    } finally {
      setBusy(false);
    }
  };


  return (
    <div className="sign-in">
      <div className="sign-in-mark" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>

      <p className="eyebrow">{stage === 'email' ? 'Sign in' : 'Check your email'}</p>
      <h1 className="page-title">VoxFaunix</h1>
      <p className="page-intro">
        An open corpus for animal communication — the signal, and the context that gives it meaning.
      </p>

      <div className="sign-in-card">
        {error && <div className="notice notice--error">{error}</div>}

        {stage === 'email' ? (
          <form onSubmit={sendCode}>
            <div className="field">
              <label className="field-label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
              <p className="field-hint">We'll send a one-time code. No password to remember.</p>
            </div>
            <button type="submit" className="btn" disabled={busy || !email.trim()}>
              {busy ? 'Sending…' : 'Send code'}
            </button>
          </form>
        ) : (
          <form onSubmit={verify}>
            <div className="field">
              <label className="field-label" htmlFor="code">
                The code we emailed you
              </label>
              <input
                id="code"
                type="text"
                required
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                style={{ maxWidth: '10rem', fontFamily: 'var(--mono)', letterSpacing: '0.2em' }}
              />
              <p className="field-hint">Sent to {email}.</p>
            </div>
            <div className="stack">
              <button type="submit" className="btn" disabled={busy || code.trim().length < 4}>
                {busy ? 'Checking…' : 'Sign in'}
              </button>
              <button
                type="button"
                className="btn btn--quiet"
                onClick={() => {
                  setStage('email');
                  setCode('');
                  setError(null);
                }}
              >
                Use a different email
              </button>
            </div>
          </form>
        )}
      </div>

      <p className="sign-in-foot">No account to create — the first code you verify sets one up.</p>
    </div>
  );
}
